import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Button, Dialog, DialogActions } from '@mui/material';
import { nanoid } from 'nanoid';
import { AccountContext } from '../context/account';
import { AddressBookItem, CreateAddressBookDetails, PaymentMethod } from '../core/global';
import { addressShape } from '../data/form-shapes';
import { getItemByKeyValue } from '../helpers/core';
import { trackEvent } from '../helpers/tracking';
import styles from "../styles/components/cart-modal.module.scss";
import { AddressBookModal, AddressBookNewModal } from './address-book-modal';
import { AddressBookSelect } from './address-book-select';
import { PaymentProvider } from './cart-modal-payment-provider';
import { CartSummaryTable, TokensSummaryTable } from './cart-summary-table';
import { InfoMessage, WarningMessage } from './error-message';
import { CreateSummary } from './form-components';
import { LoginPanel } from './login-panel';
import { ModalTitle } from './modal-title';
import { PaymentSelector } from './payment-selector';
import HorizontalStepper from './stepper';

const isExternalAddress = (userActivePaymentMethod: PaymentMethod | null) => {
  if (userActivePaymentMethod 
    && (
        userActivePaymentMethod?.type === "company-tokens" 
        || userActivePaymentMethod?.type === "subscription"
        || userActivePaymentMethod?.type === "request-approval"
        || userActivePaymentMethod?.type === "company-stripe" 
        || userActivePaymentMethod?.type === "company-po"
      )
    && userActivePaymentMethod?.config) {
      return true
  }
  return false
}

interface DeliverySummaryProps {
  setCurrentAdressBookId: React.Dispatch<React.SetStateAction<number | string | null>>
  currentAdressBookId: number | string | null
  onClose: () => void
}

export const DeliverySummary = ({
  setCurrentAdressBookId,
  currentAdressBookId,
  onClose,
}: DeliverySummaryProps) => {
  const {
    addressBook,
    userActivePaymentMethod,
  } = useContext(AccountContext);

  // const useAccountOrganisation = useMemo(() => {
  //   const {organisation} = account
  //   if (organisation) {
  //     return organisation.filter( org => org.role === 'admin' || org.role === 'super-admin')
  //   }
  //   return []
  // }, [])

  const setAddressBookForEdit = useCallback((orgId: string | number | null) => {
    setCurrentAdressBookId(orgId);
    if (orgId !== null) {
      // setShowCompanySettings(true)
    }
  }, [])
 
  const useAddressBook = useMemo(() => {
    if (userActivePaymentMethod 
      && (userActivePaymentMethod.type === "company-tokens" || userActivePaymentMethod.type === "company-stripe" || userActivePaymentMethod.type === "company-po") 
      && userActivePaymentMethod.config) {
        const {
          address,
          address_2,
          address_3,
          city,
          country,
          name,
          postcode,
        } = userActivePaymentMethod.config
        return [{
          id: nanoid(),
          address,
          address_2,
          address_3,
          city,
          country,
          name,
          postcode,
          default_address: true,
          is_external_address: true,
        } as AddressBookItem, ...addressBook]
    }
    return [...addressBook]
  }, [addressBook])

  const currentAddressBook: null | CreateAddressBookDetails = useMemo(() => {
    if (currentAdressBookId) {
      const found = useAddressBook.find( item => item.id === currentAdressBookId)
      return found ? found : null
    }
    return null
  }, [currentAdressBookId, useAddressBook])

  // When requesting payment for other company, use their company address
  if (isExternalAddress(userActivePaymentMethod) && userActivePaymentMethod?.config) {
    if (userActivePaymentMethod?.type === "request-approval") {
      return (<InfoMessage message={'Purchase request does not need a **billing address**.'} />)
    }
    return (
      <>
        <CreateSummary
          values={userActivePaymentMethod?.config}
          shape={addressShape}
        />
      </>)
  }

  return (
    <>
      <AddressBookSelect
        useAddressBook={useAddressBook}
        value={`${currentAdressBookId}`}
        id={'address-select'}
        label='Select your billing address'
        onChange={(val) => {
          setAddressBookForEdit(val);
        }}
        showEdit
      />
      {
        currentAdressBookId !== null && 
          <div className={styles.formWrapper}>
            {currentAdressBookId === "new"  ?
              <AddressBookNewModal
                onClose={() => {
                  setCurrentAdressBookId(null)
                  onClose()
                }}
              />
            :
            <CreateSummary
              values={currentAddressBook}
              shape={addressShape}
            />
          }
          {currentAdressBookId === "edit" && 
            <AddressBookModal onClose={() => {
              setCurrentAdressBookId(null)
            }} />
          }
        </div>
      }
    </>
  )
}

const steps = ['Order summary', 'Billing details', 'Payment']
interface CartModalProps {
  // currentFeature?: FeatureCollection | null
  onClose: () => void
}

export const CartModal = ({
  // currentFeature,
  onClose
}: CartModalProps) => {
  const [currentAdressBookId, setCurrentAdressBookId] = useState<number | string | null>(null)
  const [activeStep, setActiveStep] = React.useState(0);

  const {
    cart,
    addressBook,
    purchaseTokens,
    paymentType,
    setCartError,
    cartError,
    userActivePaymentMethod,
    userPaymentMethods,
  } = useContext(AccountContext);

  useEffect(() => {
    setCartError([])
  }, [])
  
  useEffect(() => {
    if (purchaseTokens.length === 0) {
      trackEvent("open-shopping-cart", {cart})
    }
  }, [cart, purchaseTokens])
  
  useEffect(() => {
    if (purchaseTokens.length > 0) {
      trackEvent("open-shopping-cart-tokens-purchase", {purchaseTokens})
    }
  }, [purchaseTokens])


  useEffect(() => {
    setCartError([])
  }, [activeStep])
  
  const noTokensToPurchase = purchaseTokens.length === 0
  const isEmpty = noTokensToPurchase && !(cart && Array.isArray(cart) && cart.length > 0)

  useEffect(() => {
    if (addressBook && Array.isArray(addressBook) && addressBook.length > 0) {
      const isExternal = isExternalAddress(userActivePaymentMethod)
      if (isExternal) {
        setCurrentAdressBookId(null)
      } else {
        const found = getItemByKeyValue(addressBook, 'default_address', true)
        if (found !== -1) {
          setCurrentAdressBookId(found.id)
        } else {
          setCurrentAdressBookId("new")
        }
      }
    } else {
      setCurrentAdressBookId("new")
    }
  }, [addressBook, currentAdressBookId, userActivePaymentMethod])

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };


  return (
    <Dialog
      id="cart-modal"
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <ModalTitle onClose={onClose}>Shopping Cart</ModalTitle>
      <div className={styles.dialogContent}>
        <LoginPanel top={50} message={'To purchase data, please **login** or **create a free account**.'} />
        {isEmpty && <WarningMessage icon={<ProductionQuantityLimitsIcon />}  message={`Your shopping cart is **empty**. Select a location on the map, click the *"Selected location information"* and click *"Add to basket"* to purchase a location data.`} />}
        {/* <ErrorMessage message={cartError} /> */}
        {!isEmpty && 
          <>
            <HorizontalStepper
              activeStep={activeStep}
              steps={steps}
            >
              <div className={styles.content}>
                <PaymentSelector
                  id="purchase-payment-select"
                />
                {purchaseTokens.length > 0 ?
                  <TokensSummaryTable data={purchaseTokens} />
                  :
                  <CartSummaryTable data={cart || []} />
                }
              </div>
              <div className={styles.content}>
                <DeliverySummary 
                  onClose={() => {
                    const emptyAddressBook = (addressBook && Array.isArray(addressBook) && addressBook.length === 0)
                    if (emptyAddressBook) {
                      handleBack()
                    }
                  }} 
                  currentAdressBookId={currentAdressBookId} 
                  setCurrentAdressBookId={setCurrentAdressBookId} 
                />
              </div>
              <div className={styles.content}>
                <PaymentProvider
                  onClose={onClose} data={cart || []} 
                  currentAdressBookId={currentAdressBookId} 
                />
              </div>
            </HorizontalStepper>
          </>}
      </div>
      <>
        <DialogActions sx={{justifyContent: 'space-between', pb: 3, px: 3}}>
          <>{isEmpty ?
            <>
            <div />
            <Button onClick={onClose}>
              Close
            </Button>
            </>
          :
            <>
              {activeStep === steps.length ? 
                <Button onClick={onClose}>Close</Button>
              : 
                <>
                  <Button
                    color="inherit"
                    onClick={activeStep === 0 ? onClose : handleBack}
                    sx={{ mr: 1 }}
                  >
                    {activeStep === 0 ? 'Cancel' : 'Back'}
                  </Button>
                  {activeStep !== steps.length - 1 && 
                    (userPaymentMethods && Array.isArray(userPaymentMethods) && userPaymentMethods.length > 0) && 
                    <Button
                      id="cart-modal-next-button"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  }
                </>
              }
            </>
          }
          </>

        </DialogActions>
      </>
    </Dialog>
  )
}
